import React from "react";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import {
  AspectRatio,
  Box,
  Button as ChakraButton,
  Flex,
  Grid,
  Icon,
  Image,
  Skeleton,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FaArrowLeft } from "react-icons/fa";
import { FiShare2 } from "react-icons/fi";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import Button from "../../components/Button";
import { P } from "../../components/Type";
import { formatMoney } from "../../utils";
import { HOSTNAME } from "../../utils/request";
import { MiniGameBuyForm } from "./MiniGameBuyForm";
import { ProductShareModal } from "../Toko/ProductShareModal";
import InfoIcon from "../../assets/info-circle-blue.svg";
import { fetchMiniGame, fetchMiniGames, miniGameDocsPath, miniGamePlayPath } from "./api";
import { MINI_GAME_DOCS_SLUGS } from "./games";

const GameArt = ({ game, size }) => {
  const [broken, setBroken] = React.useState(false);
  if (!game.image || broken) {
    return (
      <Flex
        w={size}
        h={size}
        flexShrink={0}
        align="center"
        justify="center"
        bg="saweria-yellow.normal"
        border="1px solid black"
        borderRadius="6px"
        fontSize="48px"
      >
        🎨
      </Flex>
    );
  }
  return (
    <Image
      src={game.image}
      alt={game.name}
      w={size}
      h={size}
      flexShrink={0}
      objectFit="cover"
      borderRadius="6px"
      onError={() => setBroken(true)}
    />
  );
};

// Game card on the tipper's Mini Games tab (same card style as the store products)
const MiniGameCard = ({ game, currency, onJoin }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const price = (
    <Text color="black" fontSize="16px" fontWeight="700" lineHeight="24px" isTruncated>
      {formatMoney(game.price, { currency })}
    </Text>
  );

  if (isMobile) {
    return (
      <Flex
        direction="column"
        gap="16px"
        w="100%"
        px="12px"
        py="16px"
        bg="#F2F7F5"
        borderRadius="6px"
        border="1px solid #BEC1C1"
        cursor="pointer"
        onClick={onJoin}
      >
        <Flex gap="12px" align="flex-start">
          <GameArt game={game} size="72px" />
          <Flex direction="column" gap="8px" flex="1 1 0" minW={0}>
            <Text color="black" fontSize="16px" fontWeight="700" lineHeight="24px" isTruncated>
              {game.name}
            </Text>
            <Text color="black" fontSize="14px" lineHeight="20px" noOfLines={2}>
              {game.description}
            </Text>
          </Flex>
        </Flex>
        {price}
        <Button
          marginBottom={0}
          type="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="100%"
          h="36px"
          fontSize="14px"
          backgroundColor="saweria-green.normal"
          label={t`Join Mini Games`}
        />
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap="16px"
      w="100%"
      p="16px"
      bg="#F2F7F5"
      borderRadius="6px"
      border="1px solid #BEC1C1"
      cursor="pointer"
      onClick={onJoin}
    >
      <AspectRatio ratio={1} w="100%">
        <GameArt game={game} size="100%" />
      </AspectRatio>
      <Flex direction="column" gap="16px" flex={1}>
        <Flex direction="column" gap="8px">
          <Text color="black" fontSize="16px" fontWeight="700" lineHeight="24px" isTruncated>
            {game.name}
          </Text>
          <Text color="black" fontSize="16px" lineHeight="24px">
            {game.description}
          </Text>
        </Flex>
        <Box mt="auto">{price}</Box>
      </Flex>
      {/* no onClick: the click bubbles up to the tile */}
      <Button
        marginBottom={0}
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="100%"
        h="40px"
        backgroundColor="saweria-green.normal"
        label={t`Join Games`}
      />
    </Flex>
  );
};

const GameList = ({ username, currency }) => {
  const router = useRouter();
  const { data, isLoading, isError } = useQuery(["mini-games", username], () => fetchMiniGames(username), {
    enabled: !!username,
  });

  if (isLoading) return <Skeleton h="300px" />;
  if (isError || !data) {
    return (
      <P>
        <Trans>Mini games sedang tidak tersedia.</Trans>
      </P>
    );
  }

  return (
    <Flex direction="column" gap={["17px", "24px"]}>
      <P mb={0} fontSize={["14px", "16px"]} lineHeight={["20px", "24px"]}>
        <Trans>Ayo main mini games dengan streamer dan pendukung lainnya!</Trans>
      </P>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="16px" w="100%">
        {data.games.map((game) => (
          <MiniGameCard
            key={game.slug}
            game={game}
            currency={currency}
            onJoin={() => router.push(`/${username}/mini-games/${game.slug}`)}
          />
        ))}
      </Grid>
    </Flex>
  );
};

// "Cara Bermain" (how-to page, when the game has one) | "Room Games" (the play page), both in a new tab
const GameLinks = ({ game, username, ...props }) => {
  const hasDocs = MINI_GAME_DOCS_SLUGS.includes(game.slug);
  const link = {
    target: "_blank",
    rel: "noopener noreferrer",
    color: "#3182CE",
    fontSize: ["14px", "16px"],
    fontWeight: "600",
    lineHeight: "24px",
    textDecoration: "underline",
    whiteSpace: "nowrap",
  };
  return (
    <Flex align="center" gap="12px" flexShrink={0} flexWrap="wrap" {...props}>
      {hasDocs && (
        <>
          <Flex as="a" href={miniGameDocsPath(game.slug)} align="center" gap="4px" {...link}>
            <Image src={InfoIcon.src} alt="" w="16px" h="16px" />
            <Trans>Cara Bermain</Trans>
          </Flex>
          <Box alignSelf="stretch" borderLeft="1px solid #BEC1C1" />
        </>
      )}
      <Text as="a" href={miniGamePlayPath(username, game.slug)} {...link}>
        <Trans>Room Games</Trans>
      </Text>
    </Flex>
  );
};

const CodeNote = (props) => (
  <Box bg="#EAEFED" borderRadius="4px" px="12px" py="8px" {...props}>
    <Text color="black" fontSize="14px" lineHeight="20px">
      <Trans>Kode diberikan setelah pembayaran</Trans>
    </Text>
  </Box>
);

// Game summary at the top of the order page (desktop: one row; mobile: note below)
const GameSummary = ({ game, username }) => {
  return (
    <Flex direction="column" gap="12px">
      <Flex gap={["12px", "20px"]} align={["flex-start", "center"]}>
        <GameArt game={game} size={["108px", "132px"]} />
        <Flex direction="column" gap="12px" flex="1 1 0" minW={0}>
          <Flex align="center" gap="12px">
            <Text flex="1 1 0" color="black" fontSize={["16px", "18px"]} fontWeight="700" lineHeight={["24px", "28px"]}>
              {game.name}
            </Text>
            <GameLinks game={game} username={username} display={["none", "flex"]} />
          </Flex>
          <Text color="black" fontSize="16px" lineHeight="24px">
            {game.description}
          </Text>
          <CodeNote display={["none", "block"]} alignSelf="flex-start" />
        </Flex>
      </Flex>
      {/* mobile: the column next to the image is too narrow for the links */}
      <GameLinks game={game} username={username} display={["flex", "none"]} />
      <CodeNote display={["block", "none"]} />
    </Flex>
  );
};

const GameOrder = ({ username, slug, streamerId, currency, ...formProps }) => {
  const router = useRouter();
  const { _ } = useLingui();
  const share = useDisclosure();
  const { data, isLoading, isError } = useQuery(
    ["mini-game", username, slug],
    () => fetchMiniGame(username, slug),
    { enabled: !!username && !!slug }
  );
  const back = () => router.push(`/${username}/mini-games`);

  if (isLoading) return <Skeleton h="400px" />;

  return (
    <Flex direction="column" gap="24px">
      <Flex align="center" justify="space-between">
        <ChakraButton
          bg="transparent"
          p={0}
          h="auto"
          onClick={back}
          leftIcon={<Icon as={FaArrowLeft} color="#2D3748" />}
          fontFamily="'IBM Plex Mono'"
          fontWeight="400"
          fontSize={["14px", "16px"]}
          color="#1A202C"
        >
          <Trans>Kembali</Trans>
        </ChakraButton>
        {data && (
          <Flex as="button" type="button" display={["flex", "none"]} align="center" gap="8px" fontSize="14px" onClick={share.onOpen}>
            <Icon as={FiShare2} boxSize="16px" />
            <Trans>Share</Trans>
          </Flex>
        )}
      </Flex>

      {isError || !data ? (
        <P>
          <Trans>Game tidak ditemukan.</Trans>
        </P>
      ) : (
        <>
          <GameSummary game={data.game} username={username} />

          <MiniGameBuyForm
            {...formProps}
            amount={data.game.price}
            streamerId={streamerId}
            streamerUsername={username}
            slug={slug}
            currency={currency}
          />

          <ProductShareModal
            isOpen={share.isOpen}
            onClose={share.onClose}
            username={username}
            url={`${HOSTNAME}/${username}/mini-games/${slug}`}
            message={_(t`Ayo main ${data.game.name} bareng ${username} di Saweria`)}
          />
        </>
      )}
    </Flex>
  );
};

/**
 * Mini Games tab on the streamer page:
 *  /[username]/mini-games         game list
 *  /[username]/mini-games/[slug]  order form (fixed price)
 */
const MiniGamesTab = ({ streamerUsername, ...props }) => {
  const router = useRouter();
  const { slug } = router.query;
  if (slug) return <GameOrder username={streamerUsername} slug={slug} {...props} />;
  return <GameList username={streamerUsername} currency={props.currency} />;
};

export { MiniGamesTab };
